import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Typography,
  Tabs,
  Tab,
  Tooltip,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  Close,
  Search,
  History,
  Visibility,
  Edit,
  Delete,
  Download,
  VideoLibrary,
  PictureAsPdf,
  Description,
  Save,
  CheckCircle,
  Warning,
  Add,
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import { formatDateTimeBr, formatDateBr } from '../utils/datetime';
import { useAuth } from '../contexts/AuthContext';
import RecebimentoOSContent from './RecebimentoOSContent';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'pendente', label: 'Pendente' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'recebido', label: 'Recebido' },
];

const STATUS_LABELS = {
  pendente: 'Pendente',
  em_andamento: 'Em andamento',
  recebido: 'Recebido',
};

const DESTINO_OPTIONS = [
  { value: '', label: 'Selecione o destino' },
  { value: 'despesas', label: 'Despesas' },
  { value: 'valor_materiais', label: 'Valor Materiais' },
];

const DESTINO_LABELS = {
  despesas: 'Despesas',
  valor_materiais: 'Valor Materiais',
};

export default function ConsultaRecebimentoOSModal({ open, onClose, API, token }) {
  const { hasPermission, user } = useAuth();
  const normalizeAccess = (value) =>
    (value || '')
      .toString()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  const accessCandidates = [
    user?.nivel_acesso,
    user?.nivel,
    user?.role,
    user?.nivelAcesso,
    user?.tipo,
    user?.perfil,
  ];
  const isManutencao = accessCandidates
    .map((value) => normalizeAccess(value))
    .some((value) => value.includes('manut'));
  const [mainTabIndex, setMainTabIndex] = useState(0);
  const [metricas, setMetricas] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [listaCompleta, setListaCompleta] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allowedClienteIds, setAllowedClienteIds] = useState(null);

  // Estado de alerta de edição por outro usuário
  const [lockAlert, setLockAlert] = useState(null);

  const [clienteId, setClienteId] = useState('');
  const [status, setStatus] = useState('');
  const [numeroOS, setNumeroOS] = useState('');
  const [numeroNFE, setNumeroNFE] = useState('');
  const [busca, setBusca] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [filtroNota, setFiltroNota] = useState(''); // 'ok', 'divergente', ''

  // Filtro local — sem requisição ao servidor ao clicar nos cards
  const lista = useMemo(() => {
    let result = listaCompleta;
    if (status === 'pendente') {
      result = result.filter(
        (os) =>
          (!os.status || os.status.trim() === '' || os.status.trim() === 'pendente') &&
          !os.data_recebimento
      );
    } else if (status === 'em_andamento') {
      result = result.filter((os) => os.status?.trim() === 'em_andamento');
    } else if (status === 'recebido') {
      result = result.filter(
        (os) =>
          os.status?.trim() === 'recebido' ||
          os.status?.trim() === 'fechado' ||
          !!os.data_recebimento
      );
    }
    if (filtroNota === 'ok') {
      result = result.filter(
        (os) =>
          ((os.numero_nfe && os.numero_nfe.trim() !== '') || os.notas_fiscais?.length > 0) &&
          !os.nota_divergente &&
          !os.notas_fiscais?.some((n) => n.nota_divergente)
      );
    } else if (filtroNota === 'divergente') {
      result = result.filter(
        (os) => os.nota_divergente || os.notas_fiscais?.some((n) => n.nota_divergente)
      );
    }
    return result;
  }, [listaCompleta, status, filtroNota]);

  const ITEMS_PER_PAGE = 30;
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(lista.length / ITEMS_PER_PAGE);
  const listaPaginada = useMemo(
    () => lista.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
    [lista, page]
  );

  const [auditoria, setAuditoria] = useState([]);
  const [destinosSelecionados, setDestinosSelecionados] = useState({});
  const [enviandoDestinoId, setEnviandoDestinoId] = useState(null);
  const [destinoFeedback, setDestinoFeedback] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailMode, setDetailMode] = useState('view');
  const [detailForm, setDetailForm] = useState({
    id: null,
    cliente_id: '',
    numero_os: '',
    numero_nfe: '',
    valor_nfe: '',
    descricao: '',
    status: 'pendente',
    nota_divergente: false,
  });
  const [selectedOs, setSelectedOs] = useState(null);
  const [notasEditaveis, setNotasEditaveis] = useState([]);
  const [salvandoNota, setSalvandoNota] = useState(null);
  const [novaNota, setNovaNota] = useState({
    numero_nfe: '',
    valor_nfe: '',
    nota_divergente: false,
  });
  const [adicionandoNota, setAdicionandoNota] = useState(false);

  // Estados para visualizador de mídia
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerType, setViewerType] = useState(null);
  const [viewerUrl, setViewerUrl] = useState('');
  const [viewerTitle, setViewerTitle] = useState('');

  const openViewer = (type, url, title = '') => {
    setViewerType(type);
    setViewerUrl(url);
    setViewerTitle(title);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
    setViewerType(null);
    setViewerUrl('');
    setViewerTitle('');
  };

  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);
  const canCreateDespesa = hasPermission('/despesas', 'create');
  const canUpdateDespesa = hasPermission('/despesas', 'update');
  const canCreateValorMateriais = hasPermission('/valor-materiais', 'create');
  const canUpdateValorMateriais = hasPermission('/valor-materiais', 'update');
  const buscarRef = useRef(async () => {});
  const carregarAuditoriaRef = useRef(async () => {});

  // Polling para verificar lock
  useEffect(() => {
    if (!open) return;
    const checkLock = async () => {
      try {
        const res = await fetch(`${API}/editing-lock/despesas`, { headers });
        if (res.ok) {
          const data = await res.json();
          if (data.locked && !data.own) {
            setLockAlert(`${data.by_user} está editando Despesas. Aguarde para editar aqui.`);
          } else {
            setLockAlert(null);
          }
        }
      } catch {
        /* ignora */
      }
    };
    checkLock();
    const interval = setInterval(checkLock, 5000);
    return () => clearInterval(interval);
  }, [open, API, headers]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      // Buscar clientes permitidos do usuário
      let clienteIds = null;
      try {
        const meResponse = await fetch(`${API}/me/clientes`, { headers });
        if (meResponse.ok) {
          const meData = await meResponse.json();
          clienteIds = meData.clientes || [];
        }
      } catch (e) {
        console.warn('Endpoint /me/clientes não disponível');
      }
      const idsNum = clienteIds && clienteIds.length > 0 ? clienteIds.map(Number) : null;
      setAllowedClienteIds(idsNum);
      // Carregar clientes e filtrar se houver restrição
      try {
        const res = await fetch(`${API}/clientes`, { headers });
        const data = await res.json();
        let list = Array.isArray(data) ? data : [];
        if (idsNum) {
          list = list.filter((c) => idsNum.includes(Number(c.id)));
        }
        setClientes(list);
        // Se só tem 1 cliente permitido, pré-selecionar
        if (idsNum && list.length === 1) {
          setClienteId(String(list[0].id));
        }
      } catch {
        setClientes([]);
      }
    })();
  }, [open, headers, API]);

  const carregarMetricas = React.useCallback(() => {
    const params = new URLSearchParams();
    if (allowedClienteIds && allowedClienteIds.length > 0) {
      allowedClienteIds.forEach((id) => params.append('cliente_ids[]', id));
    }
    const qs = params.toString() ? `?${params.toString()}` : '';
    fetch(`${API}/recebimento-os/metricas${qs}`, { headers })
      .then((res) => res.json())
      .then((data) => setMetricas(data))
      .catch(() => setMetricas(null));
  }, [API, headers, allowedClienteIds]);

  useEffect(() => {
    if (!open) return;
    if (allowedClienteIds === null) return; // aguardar carregamento de permissões
    carregarMetricas();
  }, [open, allowedClienteIds, carregarMetricas]);

  const abortRef = useRef(null);

  const descricaoOptions = useMemo(() => {
    const source = clienteId
      ? listaCompleta.filter((c) => String(c.cliente_id) === String(clienteId))
      : listaCompleta;
    const descs = [...new Set(source.map((c) => c.descricao).filter(Boolean))];
    return descs.sort((a, b) => a.localeCompare(b));
  }, [listaCompleta, clienteId]);

  const buscar = useCallback(
    async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (clienteId) params.append('cliente_id', clienteId);
        if (allowedClienteIds && allowedClienteIds.length > 0) {
          allowedClienteIds.forEach((id) => params.append('cliente_ids[]', id));
        }
        // status e filtroNota são aplicados localmente — não enviados ao servidor
        if (numeroOS) params.append('numero_os', numeroOS);
        if (numeroNFE) params.append('numero_nfe', numeroNFE);
        if (busca) params.append('busca', busca);
        if (dataInicio) params.append('data_inicio', dataInicio);
        if (dataFim) params.append('data_fim', dataFim);

        const url = `${API}/recebimento-os${params.toString() ? `?${params.toString()}` : ''}`;
        const res = await fetch(url, { headers, signal: controller.signal });
        const data = await res.json();
        setListaCompleta(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err.name !== 'AbortError') setListaCompleta([]);
      } finally {
        setLoading(false);
      }
    },
    [API, headers, allowedClienteIds, clienteId, numeroOS, numeroNFE, busca, dataInicio, dataFim]
  );

  const carregarAuditoria = useCallback(async () => {
    try {
      const res = await fetch(`${API}/recebimento-os/auditoria`, { headers });
      const data = await res.json();
      setAuditoria(Array.isArray(data) ? data : []);
    } catch {
      setAuditoria([]);
    }
  }, [API, headers]);

  useEffect(() => {
    buscarRef.current = buscar;
  }, [buscar]);

  useEffect(() => {
    if (!listaCompleta.length) return;

    setDestinosSelecionados((prev) => {
      const next = { ...prev };
      listaCompleta.forEach((row) => {
        if (row.destino_tipo) {
          next[row.id] = row.destino_tipo;
        } else if (!Object.prototype.hasOwnProperty.call(next, row.id)) {
          next[row.id] = '';
        }
      });
      return next;
    });
  }, [listaCompleta]);

  useEffect(() => {
    carregarAuditoriaRef.current = carregarAuditoria;
  }, [carregarAuditoria]);

  // Carregar quando cliente é selecionado; sem cliente, não faz requisição
  useEffect(() => {
    if (!open) return;
    if (!clienteId) return;
    setPage(1);
    buscarRef.current();
  }, [open, clienteId]);

  // Resetar página ao mudar filtros locais
  useEffect(() => {
    setPage(1);
  }, [status, filtroNota]);

  useEffect(() => {
    if (!open || mainTabIndex !== 1) return;
    carregarAuditoriaRef.current();
  }, [open, mainTabIndex]);

  const getClienteNome = (id) => clientes.find((c) => c.id === id)?.nome || id;
  // Mapeia 'fechado' para 'recebido' (RecebimentoOS usa 'fechado' para pedidos finalizados)
  const getStatusValue = (row) => {
    const status = row.status;
    if (status === 'fechado') return 'recebido';
    if (status) return status;
    return row.data_recebimento ? 'recebido' : 'pendente';
  };

  const sanitizeFilenamePart = (value) => {
    if (!value) return 'sem-valor';
    return String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9-_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
  };

  const formatDateForFilename = (dateStr) => {
    const date = dateStr ? new Date(dateStr) : null;
    if (!date || Number.isNaN(date.getTime())) return 'sem-data';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatTimeForFilename = (dateStr) => {
    const date = dateStr ? new Date(dateStr) : null;
    if (!date || Number.isNaN(date.getTime())) return 'sem-horario';
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${hh}-${min}`;
  };

  const handleDownloadPdf = (row) => {
    try {
      const clienteNome = sanitizeFilenamePart(getClienteNome(row.cliente_id));
      const dataParte = formatDateForFilename(row.data_recebimento || row.created_at);
      const horaParte = formatTimeForFilename(row.created_at || row.data_recebimento);
      const numeroChamado = sanitizeFilenamePart(row.numero_os || row.numero_nfe || `os-${row.id}`);
      const filename = `${clienteNome}_${dataParte}_${horaParte}_${numeroChamado}.pdf`;

      const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      let y = 15;
      const lineHeight = 7;

      const writeLine = (label, value = '') => {
        pdf.text(`${label}${value}`, 15, y);
        y += lineHeight;
      };

      pdf.setFontSize(14);
      pdf.text('Consulta OS / NFE', 15, y);
      y += 10;

      pdf.setFontSize(11);
      writeLine('ID: ', String(row.id || '-'));
      writeLine('Cliente: ', String(getClienteNome(row.cliente_id) || '-'));
      writeLine('Numero OS / Pedido: ', String(row.numero_os || '-'));
      writeLine('Numero NFE: ', String(row.numero_nfe || '-'));
      writeLine('Status: ', STATUS_LABELS[getStatusValue(row)] || 'Pendente');
      writeLine('Data: ', row.data_recebimento ? formatDateBr(row.data_recebimento) : '-');

      const descricao = String(row.descricao || '-');
      const descricaoLines = pdf.splitTextToSize(`Descricao: ${descricao}`, 180);
      pdf.text(descricaoLines, 15, y);

      pdf.save(filename);
    } catch (err) {
      console.error('Erro ao gerar PDF de OS/NFE:', err);
      alert('Erro ao gerar PDF');
    }
  };

  const abrirDetalhe = (row, mode) => {
    setSelectedOs(row);
    setDetailForm({
      id: row.id,
      cliente_id: row.cliente_id || '',
      numero_os: row.numero_os || '',
      numero_nfe: row.numero_nfe || '',
      valor_nfe: row.valor_nfe || '',
      descricao: row.descricao || '',
      status: getStatusValue(row),
      nota_divergente: row.nota_divergente || false,
    });
    // Copiar notas fiscais para edição
    setNotasEditaveis((row.notas_fiscais || []).map((n) => ({ ...n })));
    setDetailMode(mode);
    setDetailOpen(true);
  };

  const getDestinoSelecionado = (row) => destinosSelecionados[row.id] ?? row.destino_tipo ?? '';

  const canManageDestino = (row, destinoTipo) => {
    if (!destinoTipo) return false;

    if (destinoTipo === 'despesas') {
      return row.destino_tipo ? canUpdateDespesa || canCreateDespesa : canCreateDespesa;
    }

    if (destinoTipo === 'valor_materiais') {
      return row.destino_tipo
        ? canUpdateValorMateriais || canCreateValorMateriais
        : canCreateValorMateriais;
    }

    return false;
  };

  const handleDestinoChange = (rowId, value) => {
    setDestinosSelecionados((prev) => ({
      ...prev,
      [rowId]: value,
    }));
  };

  const handleEnviarDestino = async (row) => {
    const destinoTipo = getDestinoSelecionado(row);
    if (!destinoTipo) {
      setDestinoFeedback({
        severity: 'warning',
        message: 'Selecione Despesas ou Valor Materiais.',
      });
      return;
    }

    if (!canManageDestino(row, destinoTipo)) {
      setDestinoFeedback({
        severity: 'warning',
        message: 'Você não possui permissão para enviar ou alterar este destino.',
      });
      return;
    }

    setEnviandoDestinoId(row.id);
    try {
      const response = await fetch(`${API}/recebimento-os/${row.id}/enviar-destino`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ destino_tipo: destinoTipo }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Falha ao enviar para o destino selecionado.');
      }

      setDestinoFeedback({
        severity: 'success',
        message: `Registro enviado para ${DESTINO_LABELS[destinoTipo]}.`,
      });
      await buscar();
      carregarMetricas();
    } catch (error) {
      setDestinoFeedback({ severity: 'error', message: error.message || 'Erro ao enviar.' });
    } finally {
      setEnviandoDestinoId(null);
    }
  };

  const handleNotaChange = (index, field, value) => {
    setNotasEditaveis((prev) => {
      const novas = [...prev];
      novas[index] = { ...novas[index], [field]: value };
      return novas;
    });
  };

  const salvarNota = async (nota, index) => {
    setSalvandoNota(index);
    try {
      const res = await fetch(`${API}/recebimento-os/${selectedOs.id}/notas/${nota.id}`, {
        method: 'PATCH',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          numero_nfe: nota.numero_nfe,
          valor_nfe: nota.valor_nfe,
          nota_divergente: nota.nota_divergente,
        }),
      });

      if (res.ok) {
        const notaAtualizada = await res.json();
        setNotasEditaveis((prev) => {
          const novas = [...prev];
          novas[index] = notaAtualizada;
          return novas;
        });
        buscar();
        carregarMetricas();
      } else {
        const err = await res.text();
        alert(err || 'Erro ao salvar nota');
      }
    } catch (err) {
      console.error('Erro ao salvar nota:', err);
      alert('Erro ao salvar nota');
    } finally {
      setSalvandoNota(null);
    }
  };

  const adicionarNovaNota = async () => {
    if (!novaNota.numero_nfe) {
      alert('Informe o número da NFE');
      return;
    }
    setAdicionandoNota(true);
    try {
      const res = await fetch(`${API}/recebimento-os/${selectedOs.id}/notas`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          numero_nfe: novaNota.numero_nfe,
          valor_nfe: novaNota.valor_nfe || null,
          nota_divergente: novaNota.nota_divergente,
        }),
      });

      if (res.ok) {
        const notaCriada = await res.json();
        setNotasEditaveis((prev) => [...prev, notaCriada]);
        setNovaNota({ numero_nfe: '', valor_nfe: '', nota_divergente: false });
        buscar();
        carregarMetricas();
      } else {
        const err = await res.text();
        alert(err || 'Erro ao adicionar nota');
      }
    } catch (err) {
      console.error('Erro ao adicionar nota:', err);
      alert('Erro ao adicionar nota');
    } finally {
      setAdicionandoNota(false);
    }
  };

  const excluirNota = async (notaId) => {
    if (!window.confirm('Excluir esta nota fiscal?')) return;
    try {
      const res = await fetch(`${API}/recebimento-os/${selectedOs.id}/notas/${notaId}`, {
        method: 'DELETE',
        headers,
      });
      if (res.ok) {
        setNotasEditaveis((prev) => prev.filter((n) => n.id !== notaId));
        buscar();
        carregarMetricas();
      } else {
        alert('Erro ao excluir nota');
      }
    } catch (err) {
      console.error('Erro ao excluir nota:', err);
      alert('Erro ao excluir nota');
    }
  };

  const salvarDetalhe = async () => {
    try {
      const payload = {
        cliente_id: detailForm.cliente_id,
        numero_os: detailForm.numero_os,
        numero_nfe: detailForm.numero_nfe,
        valor_nfe: detailForm.valor_nfe,
        descricao: detailForm.descricao,
        status: detailForm.status,
        nota_divergente: detailForm.nota_divergente,
      };

      const res = await fetch(`${API}/recebimento-os/${detailForm.id}`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setDetailOpen(false);
        buscar();
        carregarMetricas();
      } else {
        const text = await res.text();
        alert(text || 'Erro ao salvar');
      }
    } catch (err) {
      console.error('Erro ao salvar OS/NFE:', err);
      alert('Erro ao salvar');
    }
  };

  const excluirDetalhe = async () => {
    if (!detailForm.id) return;
    if (!window.confirm('Tem certeza que deseja excluir esta OS/NFE?')) return;
    try {
      const res = await fetch(`${API}/recebimento-os/${detailForm.id}`, {
        method: 'DELETE',
        headers,
      });
      if (res.ok) {
        setDetailOpen(false);
        buscar();
        carregarMetricas();
      } else {
        const text = await res.text();
        alert(text || 'Erro ao excluir');
      }
    } catch (err) {
      console.error('Erro ao excluir OS/NFE:', err);
      alert('Erro ao excluir');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Consulta de OS / NFE</Typography>
        <Button onClick={onClose} size="small" startIcon={<Close />}>
          Fechar
        </Button>
      </DialogTitle>
      <DialogContent dividers>
        {lockAlert && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {lockAlert}
          </Alert>
        )}
        {destinoFeedback && (
          <Alert
            severity={destinoFeedback.severity}
            sx={{ mb: 2 }}
            onClose={() => setDestinoFeedback(null)}
          >
            {destinoFeedback.message}
          </Alert>
        )}
        <Tabs value={mainTabIndex} onChange={(e, v) => setMainTabIndex(v)} sx={{ mb: 2 }}>
          <Tab icon={<Search />} iconPosition="start" label="Buscar OS/NFE" />
          <Tab icon={<History />} iconPosition="start" label="Ocorrências" />
          <Tab icon={<CheckCircle />} iconPosition="start" label="Receber OS/NFE" />
        </Tabs>

        {mainTabIndex === 0 && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }} direction={{ xs: 'column', sm: 'row' }}>
              <Grid item xs={6} sm={2}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    bgcolor: 'warning.main',
                    color: 'warning.contrastText',
                    cursor: 'pointer',
                    border: status === 'pendente' ? '3px solid #e65100' : 'none',
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'scale(1.02)', boxShadow: 3 },
                  }}
                  onClick={() => {
                    setStatus(status === 'pendente' ? '' : 'pendente');
                    setFiltroNota('');
                  }}
                >
                  <Typography variant="h5">{metricas?.pendentes || 0}</Typography>
                  <Typography variant="caption">Pendentes</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    bgcolor: 'warning.light',
                    color: 'warning.contrastText',
                    cursor: 'pointer',
                    border: status === 'em_andamento' ? '3px solid #f57c00' : 'none',
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'scale(1.02)', boxShadow: 3 },
                  }}
                  onClick={() => {
                    setStatus(status === 'em_andamento' ? '' : 'em_andamento');
                    setFiltroNota('');
                  }}
                >
                  <Typography variant="h5">{metricas?.em_andamento || 0}</Typography>
                  <Typography variant="caption">Em andamento</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    bgcolor: 'success.main',
                    color: 'success.contrastText',
                    cursor: 'pointer',
                    border: status === 'recebido' ? '3px solid #1b5e20' : 'none',
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'scale(1.02)', boxShadow: 3 },
                  }}
                  onClick={() => {
                    setStatus(status === 'recebido' ? '' : 'recebido');
                    setFiltroNota('');
                  }}
                >
                  <Typography variant="h5">{metricas?.recebidos || 0}</Typography>
                  <Typography variant="caption">Recebidos</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    bgcolor: '#e8f5e9',
                    cursor: 'pointer',
                    border: filtroNota === 'ok' ? '3px solid #4caf50' : 'none',
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'scale(1.02)', boxShadow: 3 },
                  }}
                  onClick={() => {
                    setFiltroNota(filtroNota === 'ok' ? '' : 'ok');
                    setStatus('');
                  }}
                >
                  <Typography variant="h5" color="success.main">
                    {metricas?.notas_ok || 0}
                  </Typography>
                  <Typography variant="caption">Notas OK</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    bgcolor: '#fff3e0',
                    cursor: 'pointer',
                    border: filtroNota === 'divergente' ? '3px solid #ff9800' : 'none',
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'scale(1.02)', boxShadow: 3 },
                  }}
                  onClick={() => {
                    setFiltroNota(filtroNota === 'divergente' ? '' : 'divergente');
                    setStatus('');
                  }}
                >
                  <Typography variant="h5" color="warning.main">
                    {metricas?.notas_divergentes || 0}
                  </Typography>
                  <Typography variant="caption">Divergentes</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: !status && !filtroNota ? '3px solid #1976d2' : 'none',
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'scale(1.02)', boxShadow: 3 },
                  }}
                  onClick={() => {
                    setStatus('');
                    setFiltroNota('');
                  }}
                >
                  <Typography variant="h5">{metricas?.total || 0}</Typography>
                  <Typography variant="caption">Total</Typography>
                </Paper>
              </Grid>
            </Grid>

            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 260px', minWidth: { xs: '100%', sm: 240 } }}>
                <TextField
                  select
                  fullWidth
                  required
                  size="small"
                  label="Cliente *"
                  value={clienteId}
                  onChange={(e) => setClienteId(e.target.value)}
                  sx={!clienteId ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'warning.main', borderWidth: 2 } } : {}}
                  helperText={!clienteId ? 'Selecione um cliente para carregar os registros' : ''}
                  FormHelperTextProps={{ sx: { color: 'warning.dark' } }}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {clientes.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.nome}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  label="Nº OS"
                  size="small"
                  fullWidth
                  value={numeroOS}
                  onChange={(e) => setNumeroOS(e.target.value)}
                />
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  label="Nº NFE"
                  size="small"
                  fullWidth
                  value={numeroNFE}
                  onChange={(e) => setNumeroNFE(e.target.value)}
                />
              </Box>
              <Box sx={{ flex: '1 1 260px', minWidth: { xs: '100%', sm: 240 } }}>
                <Autocomplete
                  freeSolo
                  options={descricaoOptions.filter((t) =>
                    busca ? t.toLowerCase().includes(busca.toLowerCase()) : true
                  )}
                  inputValue={busca}
                  onInputChange={(_, value) => setBusca(value ?? '')}
                  onChange={(_, value) => {
                    if (value) {
                      setBusca(value);
                      buscar();
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Buscar"
                      size="small"
                      fullWidth
                      onKeyDown={(e) => e.key === 'Enter' && buscar()}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  type="date"
                  size="small"
                  label="Data Início"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                />
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  type="date"
                  size="small"
                  label="Data Fim"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                />
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  onClick={async () => {
                    setStatus('');
                    setFiltroNota('');
                    setPage(1);
                    await buscar();
                  }}
                >
                  Pesquisar
                </Button>
              </Box>
            </Box>

            <Grid container spacing={2}>
              {listaPaginada.map((row) => {
                const statusValue = getStatusValue(row);
                return (
                  <Grid item xs={12} sm={6} md={4} key={row.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: 3,
                        borderRadius: 2,
                      }}
                    >
                      <CardContent sx={{ flex: 1, p: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'start',
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            OS #{row.id} - {row.numero_os}
                          </Typography>
                          <Chip
                            label={STATUS_LABELS[statusValue] || 'Pendente'}
                            color={statusValue === 'recebido' ? 'success' : 'warning'}
                            size="small"
                          />
                        </Box>

                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          👤 {getClienteNome(row.cliente_id)}
                        </Typography>

                        {row.descricao && (
                          <Box
                            sx={{
                              mt: 1,
                              mb: 1,
                              p: 1,
                              bgcolor: '#fafafa',
                              borderRadius: 1,
                              border: '1px solid #f0f0f0',
                            }}
                          >
                            <Typography variant="caption" color="primary" display="block">
                              Descrição:
                            </Typography>
                            <Typography variant="body2" noWrap>
                              {row.descricao}
                            </Typography>
                          </Box>
                        )}

                        <Box sx={{ mt: 2, mb: 1 }}>
                          {/* Exibir NFE principal ou notas fiscais adicionais */}
                          {row.numero_nfe ? (
                            <Box
                              sx={{
                                bgcolor: row.nota_divergente ? '#fff3e0' : '#f0fdf4',
                                p: 1.5,
                                borderRadius: 1,
                                border: `1px solid ${row.nota_divergente ? '#ffcc80' : '#bbf7d0'}`,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  color={row.nota_divergente ? 'warning.main' : 'success.main'}
                                >
                                  NFE / Recebimento: {row.numero_nfe}
                                </Typography>
                                {row.nota_divergente && (
                                  <Chip label="Divergente" color="warning" size="small" />
                                )}
                              </Box>
                              {(row.valor_nfe || row.valor_pedido) && (
                                <Box sx={{ mt: 0.5 }}>
                                  {row.valor_pedido && (
                                    <Typography variant="caption" display="block">
                                      Valor Pedido: R$ {parseFloat(row.valor_pedido).toFixed(2)}
                                    </Typography>
                                  )}
                                  {row.valor_nfe && (
                                    <Typography variant="caption" display="block">
                                      Valor NFE: R$ {parseFloat(row.valor_nfe).toFixed(2)}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                Pago em:{' '}
                                {row.data_recebimento ? formatDateBr(row.data_recebimento) : '-'}
                              </Typography>
                            </Box>
                          ) : row.notas_fiscais && row.notas_fiscais.length > 0 ? (
                            <Box>
                              {row.notas_fiscais.map((nf, idx) => (
                                <Box
                                  key={nf.id || idx}
                                  sx={{
                                    bgcolor: nf.nota_divergente ? '#fff3e0' : '#f0fdf4',
                                    p: 1.5,
                                    borderRadius: 1,
                                    border: `1px solid ${nf.nota_divergente ? '#ffcc80' : '#bbf7d0'}`,
                                    mb: idx < row.notas_fiscais.length - 1 ? 1 : 0,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                      color={nf.nota_divergente ? 'warning.main' : 'success.main'}
                                    >
                                      NFE: {nf.numero_nfe}
                                    </Typography>
                                    <Chip
                                      label={nf.nota_divergente ? 'Divergente' : 'OK'}
                                      color={nf.nota_divergente ? 'warning' : 'success'}
                                      size="small"
                                    />
                                  </Box>
                                  {nf.valor_nfe && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                      Valor: R$ {parseFloat(nf.valor_nfe).toFixed(2)}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Sem NFE vinculada.
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #eee' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                              mb: 1,
                            }}
                          >
                            {row.destino_tipo ? (
                              <Chip
                                size="small"
                                color="info"
                                variant="outlined"
                                label={`Enviado para ${DESTINO_LABELS[row.destino_tipo] || row.destino_tipo}`}
                              />
                            ) : (
                              <Chip size="small" variant="outlined" label="Não enviado" />
                            )}
                            {row.destino_enviado_em && (
                              <Typography variant="caption" color="text.secondary">
                                {`Último envio: ${formatDateTimeBr(row.destino_enviado_em)}`}
                              </Typography>
                            )}
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1,
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              mb: 1.25,
                            }}
                          >
                            <FormControl size="small" sx={{ minWidth: 190, flex: '1 1 190px' }}>
                              <InputLabel>Destino</InputLabel>
                              <Select
                                value={getDestinoSelecionado(row)}
                                label="Destino"
                                onChange={(e) => handleDestinoChange(row.id, e.target.value)}
                              >
                                {DESTINO_OPTIONS.map((option) => (
                                  <MenuItem key={option.value || 'blank'} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<Save />}
                              onClick={() => handleEnviarDestino(row)}
                              disabled={
                                !getDestinoSelecionado(row) ||
                                enviandoDestinoId === row.id ||
                                !canManageDestino(row, getDestinoSelecionado(row))
                              }
                            >
                              {enviandoDestinoId === row.id
                                ? 'Enviando...'
                                : row.destino_tipo
                                  ? 'Atualizar envio'
                                  : 'Enviar'}
                            </Button>
                          </Box>

                          {getDestinoSelecionado(row) &&
                            !canManageDestino(row, getDestinoSelecionado(row)) && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 1 }}
                              >
                                Sem permissão para alterar o destino selecionado.
                              </Typography>
                            )}

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            {hasPermission('/recebimento-os', 'read') && (
                              <Tooltip title="Baixar PDF">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownloadPdf(row)}
                                  sx={{ border: '1px solid #ddd', color: '#d32f2f' }}
                                >
                                  <Download fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {hasPermission('/recebimento-os', 'read') && (
                              <Button
                                size="small"
                                startIcon={<Visibility />}
                                onClick={() => abrirDetalhe(row, 'view')}
                                sx={{ border: '1px solid #ddd' }}
                              >
                                Ver
                              </Button>
                            )}
                            {hasPermission('/recebimento-os', 'update') && !isManutencao && (
                              <Button
                                size="small"
                                startIcon={<Edit />}
                                onClick={() => abrirDetalhe(row, 'edit')}
                                sx={{ border: '1px solid #ddd' }}
                              >
                                Editar
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
              {!clienteId && listaCompleta.length === 0 && (
                <Grid item xs={12}>
                  <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Selecione um cliente para buscar OS/NFE.
                    </Typography>
                  </Box>
                </Grid>
              )}
              {clienteId && lista.length === 0 && !loading && (
                <Grid item xs={12}>
                  <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                    Nenhum registro encontrado.
                  </Box>
                </Grid>
              )}
            </Grid>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {lista.length} registro{lista.length !== 1 ? 's' : ''} — página {page} de {totalPages}
                </Typography>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => { setPage(v); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  color="primary"
                  size="small"
                  siblingCount={1}
                />
              </Box>
            )}
          </>
        )}

        {mainTabIndex === 2 && (
          <RecebimentoOSContent open={open && mainTabIndex === 2} API={API} token={token} />
        )}

        {mainTabIndex === 1 && (
          <Grid container spacing={2}>
            {auditoria.map((log) => (
              <Grid item xs={12} sm={6} md={4} key={log.id}>
                <Card sx={{ height: '100%', boxShadow: 2, borderRadius: 2 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTimeBr(log.data_hora)}
                      </Typography>
                      <Chip
                        label={`OS #${log.recebimento_os_id}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {log.usuario_nome}
                    </Typography>
                    <Typography variant="body2" color="error.main" sx={{ fontWeight: 600, mb: 1 }}>
                      {log.acao}
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: '#fafafa',
                        p: 1,
                        borderRadius: 1,
                        border: '1px solid #f0f0f0',
                      }}
                    >
                      <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                        {typeof log.detalhes === 'string'
                          ? log.detalhes
                          : JSON.stringify(log.detalhes)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            {auditoria.length === 0 && (
              <Grid item xs={12}>
                <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  Nenhuma ocorrência registrada.
                </Box>
              </Grid>
            )}
          </Grid>
        )}

        <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {detailMode === 'edit' ? 'Editar OS / NFE' : 'Visualizar OS / NFE'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Cliente</InputLabel>
                  <Select
                    value={detailForm.cliente_id}
                    label="Cliente"
                    onChange={(e) => setDetailForm({ ...detailForm, cliente_id: e.target.value })}
                    disabled={detailMode !== 'edit'}
                  >
                    {clientes.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={detailForm.status}
                    label="Status"
                    onChange={(e) => setDetailForm({ ...detailForm, status: e.target.value })}
                    disabled={detailMode !== 'edit' || isManutencao}
                  >
                    <MenuItem value="pendente">Pendente</MenuItem>
                    <MenuItem value="em_andamento">Em andamento</MenuItem>
                    <MenuItem value="recebido">Recebido</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Situação da Nota</InputLabel>
                  <Select
                    value={detailForm.nota_divergente ? 'divergente' : 'ok'}
                    label="Situação da Nota"
                    onChange={(e) =>
                      setDetailForm({
                        ...detailForm,
                        nota_divergente: e.target.value === 'divergente',
                      })
                    }
                    disabled={detailMode !== 'edit' || !detailForm.numero_nfe}
                  >
                    <MenuItem value="ok">✓ Nota OK</MenuItem>
                    <MenuItem value="divergente">⚠ Nota Divergente</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nº OS"
                  size="small"
                  fullWidth
                  value={detailForm.numero_os}
                  onChange={(e) => setDetailForm({ ...detailForm, numero_os: e.target.value })}
                  disabled={detailMode !== 'edit'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nº NFE"
                  size="small"
                  fullWidth
                  value={detailForm.numero_nfe}
                  onChange={(e) => setDetailForm({ ...detailForm, numero_nfe: e.target.value })}
                  disabled={detailMode !== 'edit'}
                />
              </Grid>
              {/* Campo de Valor NFE - só aparece quando há nota principal */}
              {detailForm.numero_nfe && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Valor NFE (R$)"
                    size="small"
                    fullWidth
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    value={detailForm.valor_nfe}
                    onChange={(e) => setDetailForm({ ...detailForm, valor_nfe: e.target.value })}
                    disabled={detailMode !== 'edit'}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  label="Descrição"
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  value={detailForm.descricao}
                  onChange={(e) => setDetailForm({ ...detailForm, descricao: e.target.value })}
                  disabled={detailMode !== 'edit'}
                />
              </Grid>
              {selectedOs?.destino_tipo && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    {`Enviado para ${DESTINO_LABELS[selectedOs.destino_tipo] || selectedOs.destino_tipo}`}
                    {selectedOs.destino_enviado_em
                      ? ` em ${formatDateTimeBr(selectedOs.destino_enviado_em)}`
                      : ''}
                  </Alert>
                </Grid>
              )}

              {/* Seção de Anexos */}
              {selectedOs &&
                ((selectedOs.fotos_urls && selectedOs.fotos_urls.length > 0) ||
                  (selectedOs.documentos_urls && selectedOs.documentos_urls.length > 0) ||
                  (selectedOs.videos_urls && selectedOs.videos_urls.length > 0) ||
                  selectedOs.foto_path ||
                  selectedOs.pdf_path) && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                      Anexos:
                    </Typography>

                    {/* Fotos em miniatura */}
                    {((selectedOs.fotos_urls && selectedOs.fotos_urls.length > 0) ||
                      selectedOs.foto_path) && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 0.5 }}
                        >
                          Fotos:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {selectedOs.foto_path && (
                            <Box
                              onClick={() =>
                                openViewer(
                                  'foto',
                                  selectedOs.foto_path?.startsWith('/')
                                    ? selectedOs.foto_path
                                    : `/${selectedOs.foto_path}`,
                                  'Foto'
                                )
                              }
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: 1,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                border: '2px solid #ddd',
                                '&:hover': { borderColor: 'primary.main' },
                              }}
                            >
                              <img
                                src={
                                  selectedOs.foto_path?.startsWith('/')
                                    ? selectedOs.foto_path
                                    : `/${selectedOs.foto_path}`
                                }
                                alt="Foto"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            </Box>
                          )}
                          {(selectedOs.fotos_urls || []).map((url, idx) => (
                            <Box
                              key={idx}
                              onClick={() => openViewer('foto', url, `Foto ${idx + 1}`)}
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: 1,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                border: '2px solid #ddd',
                                '&:hover': { borderColor: 'primary.main' },
                              }}
                            >
                              <img
                                src={url}
                                alt={`Foto ${idx + 1}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* PDFs */}
                    {((selectedOs.documentos_urls && selectedOs.documentos_urls.length > 0) ||
                      selectedOs.pdf_path) && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 0.5 }}
                        >
                          Documentos:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {selectedOs.pdf_path && (
                            <Chip
                              icon={<PictureAsPdf />}
                              label="PDF"
                              size="small"
                              onClick={() =>
                                openViewer(
                                  'pdf',
                                  selectedOs.pdf_path?.startsWith('/')
                                    ? selectedOs.pdf_path
                                    : `/${selectedOs.pdf_path}`,
                                  'PDF'
                                )
                              }
                              sx={{ cursor: 'pointer' }}
                            />
                          )}
                          {(selectedOs.documentos_urls || []).map((doc, idx) => (
                            <Chip
                              key={idx}
                              icon={<Description />}
                              label={doc.name || `Doc ${idx + 1}`}
                              size="small"
                              onClick={() =>
                                openViewer(
                                  'pdf',
                                  doc.url || doc,
                                  doc.name || `Documento ${idx + 1}`
                                )
                              }
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Vídeos */}
                    {selectedOs.videos_urls && selectedOs.videos_urls.length > 0 && (
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 0.5 }}
                        >
                          Vídeos:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {selectedOs.videos_urls.map((url, idx) => (
                            <Chip
                              key={idx}
                              icon={<VideoLibrary />}
                              label={`Vídeo ${idx + 1}`}
                              size="small"
                              onClick={() => openViewer('video', url, `Vídeo ${idx + 1}`)}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Grid>
                )}

              {/* Seção de Notas Fiscais Editáveis */}
              {notasEditaveis && notasEditaveis.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                    Notas Fiscais ({notasEditaveis.length}):
                  </Typography>
                  <Stack spacing={1.5}>
                    {notasEditaveis.map((nota, idx) => (
                      <Paper
                        key={nota.id || idx}
                        elevation={1}
                        sx={{
                          p: 1.5,
                          bgcolor: nota.nota_divergente ? '#fff8e1' : '#f1f8e9',
                          border: `1px solid ${nota.nota_divergente ? '#ffcc80' : '#c5e1a5'}`,
                        }}
                      >
                        <Grid container spacing={1.5} alignItems="center">
                          <Grid item xs={12} sm={3}>
                            <TextField
                              label="Nº NFE"
                              size="small"
                              fullWidth
                              value={nota.numero_nfe || ''}
                              onChange={(e) => handleNotaChange(idx, 'numero_nfe', e.target.value)}
                              disabled={detailMode !== 'edit'}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              label="Valor (R$)"
                              size="small"
                              fullWidth
                              type="number"
                              inputProps={{ step: '0.01', min: '0' }}
                              value={nota.valor_nfe || ''}
                              onChange={(e) => handleNotaChange(idx, 'valor_nfe', e.target.value)}
                              disabled={detailMode !== 'edit'}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Status</InputLabel>
                              <Select
                                value={nota.nota_divergente ? 'divergente' : 'ok'}
                                label="Status"
                                onChange={(e) =>
                                  handleNotaChange(
                                    idx,
                                    'nota_divergente',
                                    e.target.value === 'divergente'
                                  )
                                }
                                disabled={detailMode !== 'edit'}
                              >
                                <MenuItem value="ok">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CheckCircle fontSize="small" color="success" /> OK
                                  </Box>
                                </MenuItem>
                                <MenuItem value="divergente">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Warning fontSize="small" color="warning" /> Divergente
                                  </Box>
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            {detailMode === 'edit' && (
                              <Stack direction="row" spacing={0.5}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<Save />}
                                  onClick={() => salvarNota(nota, idx)}
                                  disabled={salvandoNota === idx}
                                  sx={{ flex: 1 }}
                                >
                                  {salvandoNota === idx ? '...' : 'Salvar'}
                                </Button>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => excluirNota(nota.id)}
                                  title="Excluir nota"
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Stack>
                            )}
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>
                </Grid>
              )}

              {/* Formulário para adicionar nova nota fiscal */}
              {detailMode === 'edit' && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                    + Adicionar Nova Nota Fiscal:
                  </Typography>
                  <Paper
                    elevation={1}
                    sx={{ p: 1.5, bgcolor: '#f5f5f5', border: '1px dashed #ccc' }}
                  >
                    <Grid container spacing={1.5} alignItems="center">
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="Nº NFE"
                          size="small"
                          fullWidth
                          value={novaNota.numero_nfe}
                          onChange={(e) => setNovaNota({ ...novaNota, numero_nfe: e.target.value })}
                          placeholder="Informe o número"
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="Valor (R$)"
                          size="small"
                          fullWidth
                          type="number"
                          inputProps={{ step: '0.01', min: '0' }}
                          value={novaNota.valor_nfe}
                          onChange={(e) => setNovaNota({ ...novaNota, valor_nfe: e.target.value })}
                          placeholder="0.00"
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={novaNota.nota_divergente ? 'divergente' : 'ok'}
                            label="Status"
                            onChange={(e) =>
                              setNovaNota({
                                ...novaNota,
                                nota_divergente: e.target.value === 'divergente',
                              })
                            }
                          >
                            <MenuItem value="ok">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CheckCircle fontSize="small" color="success" /> OK
                              </Box>
                            </MenuItem>
                            <MenuItem value="divergente">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Warning fontSize="small" color="warning" /> Divergente
                              </Box>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={<Add />}
                          onClick={adicionarNovaNota}
                          disabled={adicionandoNota || !novaNota.numero_nfe}
                          fullWidth
                        >
                          {adicionandoNota ? 'Adicionando...' : 'ADICIONAR'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2 }}>
            <Button onClick={() => setDetailOpen(false)}>Fechar</Button>
            {detailMode === 'view' &&
              hasPermission('/recebimento-os', 'update') &&
              !isManutencao && (
                <Button variant="outlined" onClick={() => setDetailMode('edit')}>
                  Editar
                </Button>
              )}
            {detailMode === 'edit' &&
              hasPermission('/recebimento-os', 'update') &&
              !isManutencao && (
                <Button variant="contained" onClick={salvarDetalhe}>
                  Salvar
                </Button>
              )}
            {hasPermission('/recebimento-os', 'delete') && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={excluirDetalhe}
              >
                Excluir
              </Button>
            )}
          </Box>
        </Dialog>

        {/* Modal Visualizador de Mídia */}
        <Dialog open={viewerOpen} onClose={closeViewer} maxWidth="lg" fullWidth>
          <DialogTitle
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            {viewerTitle || 'Visualizador'}
            <IconButton onClick={closeViewer}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
              bgcolor: '#000',
            }}
          >
            {viewerType === 'foto' && (
              <img
                src={viewerUrl}
                alt="Visualização"
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
            )}
            {viewerType === 'pdf' && (
              <iframe
                src={viewerUrl}
                title="PDF Viewer"
                style={{ width: '100%', height: '70vh', border: 'none' }}
              />
            )}
            {viewerType === 'video' && (
              <video controls style={{ maxWidth: '100%', maxHeight: '70vh' }}>
                <source src={viewerUrl} type="video/mp4" />
                Seu navegador não suporta vídeos.
              </video>
            )}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
